import { Session } from '@benzinga/session';
import { Subscribable, SubscribableEventType, Subscription } from '@benzinga/subscribable';
import { RefreshInterval, ScannerConfig } from '@benzinga/scanner-config-manager';
import { ScannerSocket } from './socket';
import { QuoteProtos, ScannerProtos } from './scanner_protos';
import { configToQuery } from './utils';
import { delta, getNextQuotes } from './compare';
import { ScannerRequest, ScannerRequestErrorData } from './request';
import { QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';
import { SafeError } from '@benzinga/safe-await';

interface ScannerFeedRowUpdateEvent {
  rows: QuoteProtos.IQuote[];
  config: ScannerConfig;
  totalInstruments: number;
  transactions: ScannerProtos.IRowUpdatesV2;
  type: 'data_update';
}

/** A poll went unanswered; the rows last published stay in place but are no longer current. */
interface ScannerFeedStaleEvent {
  /** When the last successful response was applied (ms epoch), if any. */
  lastUpdatedAt?: number;
  /** Delay before the next attempt, in ms. */
  retryInMs: number;
  type: 'data_stale';
}

/** The back end rejected the poll (4xx). Repeating it cannot succeed, so polling stops until a refresh. */
interface ScannerFeedRequestRejectedEvent {
  error: SafeError;
  errorType: 'scanner:request_rejected';
  type: 'error';
}

export type ScannerFeedEvent =
  | ScannerFeedRowUpdateEvent
  | ScannerFeedStaleEvent
  | ScannerFeedRequestRejectedEvent
  | Exclude<SubscribableEventType<ScannerSocket>, { type: 'row_update' }>;

type PollOutcome = { outcome: 'ok' } | { outcome: 'unanswered' } | { error: SafeError; outcome: 'rejected' };

const isRealTime = (interval: number) => interval === RefreshInterval['Real-time'];

/**
 * How long a poll may stay unanswered before it is given up on. Deliberately longer than the
 * back end's own timeout on /rest/v2/scanner/us/equity/scan (10s), so a slow query ends with the
 * server's answer rather than a client abort of work it is still computing; this limit only
 * catches responses that never arrive at all.
 */
export const SCANNER_RESPONSE_TIMEOUT_MS = 15_000;
/** First wait after an unanswered poll; doubles per consecutive failure up to the refresh interval. */
export const SCANNER_BACKOFF_START_MS = 3000;
/** Backoff ceiling when there is no refresh interval to cap it (refreshInterval = Never). */
const MAX_BACKOFF_WITHOUT_INTERVAL_MS = 60_000;

const refreshIntervalMs = (interval: number): number | undefined => (interval > 0 ? interval * 1000 : undefined);

/**
 * A 4xx means the request itself is wrong (invalid query, no access) — sending it again cannot
 * succeed. 408/429 are the server asking us to come back later, so they back off like timeouts,
 * network errors and 5xx do.
 */
const isRejected = (error: SafeError): boolean => {
  const status = (error.data as ScannerRequestErrorData | undefined)?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

export class ScannerFeed extends Subscribable<ScannerFeedEvent> {
  private config: ScannerConfig;
  private session: Session;
  private request: ScannerRequest;

  /** Only created and used when refreshInterval is Real-time - prevents REST requests from touching WebSocket */
  private socket?: ScannerSocket;
  private socketSubscription?: Subscription<ScannerSocket>;
  /** The single pending poll — either the next scheduled refresh or a backoff retry. */
  private pollTimer?: ReturnType<typeof setTimeout>;
  private polling = false;
  /** The poll currently awaiting a response. At most one exists at a time. */
  private inFlight?: { controller: AbortController; id: number };
  /** Bumped per poll; a response whose id no longer matches was given up on and is dropped. */
  private pollId = 0;
  private consecutiveFailures = 0;
  private lastUpdatedAt?: number;

  private previousQuotes: QuoteProtos.IQuote[] = [];
  /** Next row_update is a full session snapshot relative to empty state (reconnect or new socket). */
  private pendingReconnectSnapshot = false;
  private fireOnZeroSubscription: () => void;

  constructor(session: Session, config: ScannerConfig, request: ScannerRequest, onZeroSubscriptions: () => void) {
    super();
    this.config = config;
    this.session = session;
    this.request = request;
    this.fireOnZeroSubscription = onZeroSubscriptions;

    const sub = this.session.getManager(QuotesV3FieldsManager).subscribe(event => {
      switch (event.type) {
        case 'quotes_fields:update':
          // Fields can arrive after the feed is already live. Replace the socket only when
          // subscribed — and always tear down the previous one first so we do not stack WS
          // connections while the old close is still in flight.
          if (isRealTime(this.config.refreshInterval) && this.socketSubscription) {
            this.replaceRealtimeSocket();
          }
          this.refresh();
      }
    });

    session.onPreSessionClose(() => sub.unsubscribe());
  }

  public refresh = () => {
    switch (this.config.refreshInterval) {
      case RefreshInterval['Real-time']:
        break;
      case RefreshInterval['Never']:
      case RefreshInterval['10s']:
      case RefreshInterval['1m']:
      case RefreshInterval['30s']:
      default:
        this.requestRefresh();
        break;
    }
  };

  public getConfig = () => this.config;
  public getPrevious = () => this.previousQuotes;

  protected onFirstSubscription() {
    if (isRealTime(this.config.refreshInterval)) {
      this.replaceRealtimeSocket();
      return;
    }
    this.startPollingMode();
  }

  protected onZeroSubscriptions() {
    this.teardownRealtimeSocket();
    this.stopPolling();
    this.previousQuotes = [];
    this.pendingReconnectSnapshot = false;
    this.fireOnZeroSubscription();
  }

  private startPollingMode = (): void => {
    this.polling = true;
    this.poll();
  };

  private stopPolling = (): void => {
    this.polling = false;
    this.clearPollTimer();
    this.abandonInFlight();
    this.consecutiveFailures = 0;
    this.lastUpdatedAt = undefined;
  };

  /**
   * On-demand refresh (toolbar, fields update). Goes through the same gate as a scheduled poll:
   * it never stacks on an outstanding request, and while backing off the pending retry stands
   * instead of being pulled forward — otherwise repeated clicks would defeat the backoff.
   */
  private requestRefresh = (): void => {
    const backingOff = this.consecutiveFailures > 0 && this.pollTimer !== undefined;
    if (this.inFlight || backingOff) {
      return;
    }
    this.clearPollTimer();
    this.poll();
  };

  private clearPollTimer = (): void => {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = undefined;
    }
  };

  /** Drop the outstanding request: abort the fetch and make sure its response is never applied. */
  private abandonInFlight = (): void => {
    if (this.inFlight) {
      this.inFlight.controller.abort();
      this.inFlight = undefined;
    }
    this.pollId++;
  };

  /**
   * Single chained timer (not setInterval): the next poll is only scheduled once the current one
   * resolves, so a slow back end never has requests piling onto it, and recovering from an outage
   * sends one request rather than one per missed interval.
   */
  private schedulePoll = (delayMs: number): void => {
    this.clearPollTimer();
    if (!this.polling) {
      return;
    }
    this.pollTimer = setTimeout(() => {
      this.pollTimer = undefined;
      this.poll();
    }, delayMs);
  };

  /** Read at call time so a changed refresh interval applies to the very next wait. */
  private backoffDelayMs = (): number => {
    const intervalMs = refreshIntervalMs(this.config.refreshInterval);
    const ceiling = intervalMs ?? MAX_BACKOFF_WITHOUT_INTERVAL_MS;
    return Math.min(SCANNER_BACKOFF_START_MS * 2 ** (this.consecutiveFailures - 1), ceiling);
  };

  private poll = (): void => {
    if (this.inFlight) {
      return;
    }
    // Without scanner defs the query cannot be built yet. That is neither an error nor an
    // unanswered request — the quotes_fields:update handler refreshes once the defs land.
    if (!this.session.getManager(QuotesV3FieldsManager).getCachedScannerDefs()) {
      return;
    }
    const id = ++this.pollId;
    const controller = new AbortController();
    this.inFlight = { controller, id };

    const giveUpTimer = setTimeout(() => {
      if (this.inFlight?.id === id) {
        this.abandonInFlight();
        this.onPollFailed();
      }
    }, SCANNER_RESPONSE_TIMEOUT_MS);

    this.getQuotes(controller.signal)
      .catch((): PollOutcome => ({ outcome: 'unanswered' }))
      .then(result => {
        clearTimeout(giveUpTimer);
        // Given up on (timeout, teardown, or superseded) — a late response must not touch the screen.
        if (this.pollId !== id) {
          return;
        }
        this.inFlight = undefined;
        switch (result.outcome) {
          case 'ok':
            this.onPollSucceeded();
            break;
          case 'rejected':
            this.onPollRejected(result.error);
            break;
          case 'unanswered':
            this.onPollFailed();
            break;
        }
      });
  };

  private onPollSucceeded = (): void => {
    this.consecutiveFailures = 0;
    const intervalMs = refreshIntervalMs(this.config.refreshInterval);
    if (intervalMs !== undefined) {
      this.schedulePoll(intervalMs);
    }
  };

  private onPollFailed = (): void => {
    this.consecutiveFailures++;
    const retryInMs = this.backoffDelayMs();
    // Rows already published stay as they are; only flag them as not current.
    this.dispatch({ lastUpdatedAt: this.lastUpdatedAt, retryInMs, type: 'data_stale' });
    this.schedulePoll(retryInMs);
  };

  /**
   * No retry and no scheduled refresh: the same request would be rejected again. Rows stay in
   * place and the real error is surfaced; an on-demand refresh (or a config change) tries again.
   */
  private onPollRejected = (error: SafeError): void => {
    this.consecutiveFailures = 0;
    this.clearPollTimer();
    this.dispatch({ error, errorType: 'scanner:request_rejected', type: 'error' });
  };

  private replaceRealtimeSocket = (): void => {
    this.teardownRealtimeSocket();
    // New sessions (fields refresh or first subscribe) send SNAPSHOT_DELTAS from empty
    // state — same as post-reconnect. Apply the first row_update from [] so we do not
    // merge snapshot adds onto stale previousQuotes.
    this.pendingReconnectSnapshot = true;
    this.socket = new ScannerSocket(this.session, {
      query: configToQuery(this.config, this.session).ok ?? { limit: 1 },
      updateType: ScannerProtos.ScannerConfig.UpdateType.SNAPSHOT_DELTAS,
    });
    this.socketSubscription = this.socket.subscribe(this.onScannerMessage);
  };

  private teardownRealtimeSocket = (): void => {
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.socket = undefined;
  };

  private onScannerMessage = (event: SubscribableEventType<ScannerSocket>) => {
    switch (event.type) {
      case 'on-status-change':
        this.dispatch(event);
        if (event.status === 'reconnected') {
          // Keep previousQuotes / do not emit a remove-all data_update. Wiping here blanks
          // the grid until the post-reconnect snapshot arrives (same empty-grid flash signals
          // avoided). The new session still sends SNAPSHOT_DELTAS relative to empty state —
          // apply that next row_update from [] without an intermediate blank publish.
          this.pendingReconnectSnapshot = true;
        }
        break;
      case 'row_update': {
        const baseQuotes = this.pendingReconnectSnapshot ? [] : this.previousQuotes;
        this.pendingReconnectSnapshot = false;
        this.previousQuotes = getNextQuotes(baseQuotes, event.rows);
        this.dispatch({
          config: this.config,
          rows: this.previousQuotes,
          totalInstruments: event.rows.total ?? 0,
          transactions: event.rows,
          type: 'data_update',
        });
        break;
      }
      default:
        this.dispatch(event);
        break;
    }
  };

  /** Fetches only; applying the result is left to `applyQuotes` so a given-up poll can be dropped. */
  private getQuotes = async (signal: AbortSignal): Promise<PollOutcome> => {
    const quotes = await this.request.getScannerInstruments(this.config, { retry: false, signal });
    if (signal.aborted) {
      return { outcome: 'unanswered' };
    }
    if (quotes.err) {
      return isRejected(quotes.err) ? { error: quotes.err, outcome: 'rejected' } : { outcome: 'unanswered' };
    }
    this.applyQuotes(quotes.ok);
    return { outcome: 'ok' };
  };

  private applyQuotes = (result: ScannerProtos.IScannerResult): void => {
    const prevQuotes = this.previousQuotes;
    this.previousQuotes = result.instruments ?? [];
    this.lastUpdatedAt = Date.now();
    this.dispatch({
      config: this.config,
      rows: this.previousQuotes,
      totalInstruments: result.totalInstruments ?? 0,
      transactions: delta(prevQuotes, this.previousQuotes, this.session),
      type: 'data_update',
    });
  };
}
